import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, throwError, firstValueFrom } from 'rxjs';
import { catchError, switchMap, tap, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
    ClientDto,
    ClientGender,
    CreateClientDto,
    ClientType,
    ClientTypeApi,
    ClientTypeInput,
    PaginatedResponse,
    UpdateClientDto
} from '../../../models/client.model';
import { DocumentType } from 'src/app/pages/employees/enums/document-type.enum';
import { AuthService } from 'src/app/auth/auth.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { normalizeClientType } from 'src/app/core/utils/client.utils';

interface BackendPaginatedClientItemDto {
    id: number;
    clientFullName?: string | null;
    clientEmail?: string | null;
    email?: string | null;
    clientPhone?: string | null;
    status?: 1 | 2 | '1' | '2' | null;
    clientType?: ClientTypeInput;
    clientPhoneNumber?: string | null;
    phoneNumber?: string | null;
    clientDocumentId?: string | null;
    documentId?: string | null;
    lastVisit?: string | null;
    gender?: string | null;
    birthday?: string | null;
    clientGender?: string | null;
    birthDate?: string | null;
}

interface BackendPaginatedClientsResponse {
    items: BackendPaginatedClientItemDto[];
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

interface BackendSearchClientItemDto {
    id: number;
    clientFullName?: string | null;
    clientEmail?: string | null;
    email?: string | null;
    clientPhoneNumber?: string | null;
    clientPhone?: string | null;
    phoneNumber?: string | null;
    clientDocumentId?: string | null;
    documentId?: string | null;
}

export interface ClientSearchOption {
    id: number;
    fullName: string;
    clientFullName: string;
    email?: string;
    clientEmail?: string;
    phoneNumber?: string;
    clientPhoneNumber?: string;
    clientDocumentId?: string;
    documentId?: string;
}

type ClientStatusFilter = 1 | 2;

@Injectable({
    providedIn: 'root'
})
export class ClientsService {
    private readonly apiUrl = `${environment.cafler_backoffice_back_url}/clients`;
    private readonly paginatedApiUrl = `${this.apiUrl}/paginated`;

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly snackbarService: SnackbarService,
        private readonly translate: TranslateService,
        private readonly analytics: AnalyticsService
    ) { }

    getAll(
        page = 1,
        size = 20,
        searchTerm?: string,
        clientStatus?: ClientStatusFilter
    ): Observable<PaginatedResponse<ClientDto>> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) => {
                let params = new HttpParams()
                    .set('pageNumber', String(page))
                    .set('pageSize', String(size));

                const trimmed = String(searchTerm ?? '').trim();

                if (trimmed.length > 0) {
                    params = params.set('searchText', trimmed);
                }

                if (clientStatus !== undefined && clientStatus !== null) {
                    params = params.set('clientStatus', String(clientStatus));
                }

                return this.http.get<BackendPaginatedClientsResponse>(this.paginatedApiUrl, {
                    headers,
                    params
                });
            }),
            map((response) => ({
                items: (response.items ?? []).map((item) => this.mapPaginatedClient(item)),
                metadata: {
                    currentPage: response.currentPage ?? page,
                    pageSize: response.pageSize ?? size,
                    totalPages: response.totalPages ?? 1,
                    totalCount: response.totalItems ?? 0,
                    hasPrevious: Boolean(response.hasPreviousPage),
                    hasNext: Boolean(response.hasNextPage)
                }
            })),
            catchError(this.handleError('getAll', 'clients.error_load_clients'))
        );
    }

    searchClients(
        query: string,
        centerId?: number,
        clientStatus?: ClientStatusFilter
    ): Observable<ClientSearchOption[]> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) => {
                let params = new HttpParams();
                const trimmed = String(query ?? '').trim();

                if (trimmed.length > 0) {
                    params = params.set('searchText', trimmed);
                }

                if (centerId !== undefined && centerId !== null) {
                    params = params.set('centerId', String(centerId));
                }

                if (clientStatus !== undefined && clientStatus !== null) {
                    params = params.set('clientStatus', String(clientStatus));
                }

                return this.http.get<BackendSearchClientItemDto[] | BackendPaginatedClientsResponse>(this.apiUrl, {
                    headers,
                    params
                });
            }),
            map((response) => {
                const items = Array.isArray(response) ? response : (response.items ?? []);
                return items.map((client) => {
                    const email = String(client.clientEmail ?? client.email ?? '').trim() || undefined;
                    const phoneNumber = String(
                        client.clientPhoneNumber ?? client.clientPhone ?? client.phoneNumber ?? ''
                    ).trim() || undefined;
                    const documentId = String(client.clientDocumentId ?? client.documentId ?? '').trim() || undefined;
                    const fullName = this.resolveDisplayName(client.clientFullName, email, undefined, client.id);
                    return {
                        id: client.id,
                        fullName,
                        clientFullName: String(client.clientFullName ?? '').trim() || fullName,
                        email,
                        clientEmail: email,
                        phoneNumber,
                        clientPhoneNumber: phoneNumber,
                        clientDocumentId: documentId,
                        documentId
                    };
                });
            }),
            catchError(this.handleError('searchClients', 'clients.error_load_clients'))
        );
    }

    getById(id: number): Observable<ClientDto> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.get<ClientDto>(`${this.apiUrl}/${encodeURIComponent(String(id))}`, {
                    headers
                })
            ),
            map((client) => this.normalizeClientFromDetail(client)),
            catchError(this.handleError('getById', 'clients.error_load_client'))
        );
    }

    create(client: CreateClientDto): Observable<ClientDto> {
        const payload = this.normalizeClientPayload(this.normalizeClientDocumentType(client));
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.post<ClientDto>(this.apiUrl, payload, {
                    headers
                })
            ),
            tap((resp: ClientDto) => {
                this.analytics.track(AnalyticsEvent.CustomerCreateSuccess, {
                    ...this.analytics.globalProps(),
                    customer_id: resp.id,
                });

            }),

            catchError((err: HttpErrorResponse) => {
                const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
                const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

                this.analytics.track(AnalyticsEvent.CustomerCreateFail, {
                    ...this.analytics.globalProps(),
                    error_code: errorCode,
                    error_message: errorMessage,
                    http_status: err.status
                });

                return this.handleError('create', 'clients.error_create_client')(err);
            })
        );
    }

    update(id: number, client: UpdateClientDto): Observable<ClientDto> {
        const payload = this.normalizeClientPayload(this.normalizeClientDocumentType(client));
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.put<ClientDto>(`${this.apiUrl}/${encodeURIComponent(String(id))}`, payload, {
                    headers
                })
            ),
            catchError(this.handleError('update', 'clients.error_update_client'))
        );
    }

    activate(id: number): Observable<ClientDto> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.put<ClientDto>(`${this.apiUrl}/${encodeURIComponent(String(id))}/activate`, {}, {
                    headers
                })
            ),
            tap((resp: ClientDto) => {
                this.analytics.track(AnalyticsEvent.CustomersView, {
                    ...this.analytics.globalProps()
                });
            }),
            catchError((err: HttpErrorResponse) => {
                console.error(`ClientsService activate failed:`, err);
                return this.handleError('activate', 'clients.error_activate_client')(err);
            })
        );
    }

    deactivate(id: number): Observable<ClientDto> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.put<ClientDto>(`${this.apiUrl}/${encodeURIComponent(String(id))}/deactivate`, {}, {
                    headers
                })
            ),
            tap((resp: ClientDto) => {
                this.analytics.track(AnalyticsEvent.CustomersView, {
                    ...this.analytics.globalProps()
                });
            }),
            catchError((err: HttpErrorResponse) => {
                console.error(`ClientsService deactivate failed:`, err);
                return this.handleError('deactivate', 'clients.error_deactivate_client')(err);
            })
        );
    }

    sendLineInvitation(id: number): Observable<void> {
        return this.withAuthHeaders$().pipe(
            switchMap((headers) =>
                this.http.post<{ code: string }>(
                    `${this.apiUrl}/${encodeURIComponent(String(id))}/line-link-code`,
                    null,
                    { headers }
                )
            ),
            map(() => void 0)
        );
    }

    getCreateClientDtoFromClientDto(clientInfo: ClientDto): CreateClientDto {
        return {
            fullName: this.resolveDisplayName(clientInfo.fullName, clientInfo.email, clientInfo.phoneNumber, clientInfo.id),
            email: clientInfo.email || null,
            phoneNumber: clientInfo.phoneNumber ?? '',
            documentType: this.normalizeDocumentTypeValue(clientInfo.documentType),
            documentId: clientInfo.documentId ?? '',
            address: clientInfo.address ?? undefined,
            clientType: this.toApiClientTypeValue(clientInfo.clientType),
            receiveMaintenanceData: Boolean(clientInfo.receiveMaintenanceData),
            preferredContactMethod: clientInfo.preferredContactMethod,
            firstRegistrationDate: clientInfo.firstRegistrationDate,
            gender: this.normalizeGenderValue(clientInfo.gender),
            birthday: clientInfo.birthday ?? undefined,
            comments: clientInfo.comments,
            secondaryContactName: clientInfo.secondaryContactName,
            secondaryContactPhone: clientInfo.secondaryContactPhone
        };
    }

    async getClientById(id: number): Promise<ClientDto | null> {
        try {
            const client = await firstValueFrom(this.getById(id));
            return client ?? null;
        } catch (err) {
            console.error('Error fetching client by id', err);
            return null;
        }
    }

    private withAuthHeaders$(): Observable<HttpHeaders> {
        return from(this.authService.getAccessToken()).pipe(
            map((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return new HttpHeaders({
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                });
            })
        );
    }

    private handleError(operation: string, translateKey: string) {
        return (error: unknown) => {
            console.error(`ClientsService ${operation} failed:`, error);

            const userMessage = this.translate.instant(translateKey);
            this.snackbarService.showError(userMessage);

            return throwError(() => {
                if (error instanceof Error) return error;
                try {
                    return new Error(JSON.stringify(error));
                } catch {
                    return new Error(String(error));
                }
            });
        };
    }

    private normalizeClientDocumentType<T extends { documentType: unknown }>(client: T): T & { documentType: number } {
        return {
            ...client,
            documentType: this.normalizeDocumentTypeValue(client.documentType)
        } as T & { documentType: number };
    }

    private normalizeClientPayload<T extends { fullName?: unknown }>(client: T): T & { fullName: string } {
        const explicitFullName = String(client.fullName ?? '').trim();

        return {
            ...client,
            fullName: explicitFullName
        } as T & { fullName: string };
    }

    private mapPaginatedClient(item: BackendPaginatedClientItemDto): ClientDto {
        return {
            id: item.id,
            fullName: this.resolveDisplayName(item.clientFullName, item.clientEmail, item.clientPhone, item.id),
            email: item.clientEmail ?? '',
            phoneNumber: item.clientPhone ?? '',
            status: item.status ?? undefined,
            clientType: item.clientType,
            lastVisit: item.lastVisit ?? undefined,
            gender: this.normalizeGenderValue(item.gender ?? item.clientGender),
            birthday: item.birthday ?? item.birthDate ?? undefined,
            centerId: 0,
            receiveMaintenanceData: false,
            clientFullName: item.clientFullName ?? undefined
        };
    }

    private normalizeClientFromDetail(client: ClientDto): ClientDto {
        return {
            ...client,
            fullName: this.resolveDisplayName(client.fullName, client.email, client.phoneNumber, client.id),
            email: client.email ?? '',
            phoneNumber: client.phoneNumber ?? '',
            clientType: normalizeClientType(client.clientType),
            gender: this.normalizeGenderValue(client.gender),
            birthday: client.birthday ?? undefined
        };
    }

    private toApiClientTypeValue(value: ClientTypeInput | undefined): ClientTypeApi {
        return normalizeClientType(value) === ClientType.B2B
            ? ClientTypeApi.Company
            : ClientTypeApi.Individual;
    }

    private normalizeGenderValue(value: unknown): ClientGender {
        const raw = String(value ?? '').trim().toLowerCase();
        if (!raw) return 'unspecified';
        if (raw === 'male' || raw === 'm' || raw === 'hombre' || raw === 'man') return 'male';
        if (raw === 'female' || raw === 'f' || raw === 'mujer' || raw === 'woman') return 'female';
        if (raw === 'other' || raw === 'otro') return 'other';
        if (raw === 'unspecified' || raw === 'unknown' || raw === 'prefer_not_to_say' || raw === 'na') return 'unspecified';
        return 'other';
    }

    private normalizeDocumentTypeValue(value: unknown): number {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        const raw = String(value ?? '').trim().toLowerCase();
        if (!raw) return DocumentType.WithoutDocument;

        const map: Record<string, DocumentType> = {
            '0': DocumentType.WithoutDocument,
            'withoutdocument': DocumentType.WithoutDocument,
            'dni': DocumentType.DNI,
            '1': DocumentType.DNI,
            'nie': DocumentType.NIE,
            '10': DocumentType.NIE,
            'cni': DocumentType.CNI,
            '2': DocumentType.CNI,
            'nin': DocumentType.NIN,
            '3': DocumentType.NIN,
            'rut': DocumentType.RUT,
            '4': DocumentType.RUT,
            'cedula': DocumentType.Cedula,
            '5': DocumentType.Cedula,
            'dimex': DocumentType.DIMEX,
            '6': DocumentType.DIMEX,
            'ine': DocumentType.INE,
            '7': DocumentType.INE,
            'curp': DocumentType.CURP,
            '8': DocumentType.CURP,
            'passport': DocumentType.Passport,
            'pasaporte': DocumentType.Passport,
            '9': DocumentType.Passport,
            'pt_cartao': DocumentType.PT_CARTAO,
            'ptcartao': DocumentType.PT_CARTAO,
            'cartao': DocumentType.PT_CARTAO,
            'cartão': DocumentType.PT_CARTAO,
            'cc': DocumentType.PT_CARTAO,
            '15': DocumentType.PT_CARTAO,
            'cif': DocumentType.CIF,
            '11': DocumentType.CIF,
            'other': DocumentType.Other,
            'otro': DocumentType.Other,
            '12': DocumentType.Other
        };

        if (map[raw] !== undefined) return map[raw];

        const parsed = Number(raw);
        return Number.isNaN(parsed) ? DocumentType.WithoutDocument : parsed;
    }

    private resolveDisplayName(
        fullName?: string | null,
        email?: string | null,
        phone?: string | null,
        id?: number
    ): string {
        const explicit = String(fullName ?? '').trim();
        if (explicit) return explicit;

        const emailValue = String(email ?? '').trim();
        if (emailValue) return emailValue;

        const phoneValue = String(phone ?? '').trim();
        if (phoneValue) return phoneValue;

        return this.translate.instant('clients.client_number', { id: id ?? '' }).trim();
    }


    async searchClientsAsync(
        query: string,
        centerId?: number,
        clientStatus?: ClientStatusFilter
    ): Promise<ClientSearchOption[]> {
        return firstValueFrom(this.searchClients(query, centerId, clientStatus));
    }
}
